/**
 * What a proposal DOES, declared before the vote and applied by it.
 *
 * Turns `6a` and `6b` are one mechanism drawn twice: `6a` is what a commissioner declares on the
 * docket beforehand, `6b` is the configuration table that the vote then produces. Building either
 * alone gets you half of it, so they are one module.
 *
 * THE PROBLEM IT SOLVES. Today a rule passes in a room and somebody later remembers to change a
 * number in `config.ts`. Between those two moments the app and the rulebook disagree and nothing
 * says so. Worse, the change is a straight edit: the 2024 keeper costs move because the 2026
 * value moved, and History quietly rewrites itself.
 *
 * TWO IDEAS FIX BOTH.
 *
 *   A SEASON IS A VERSION, NOT AN EDIT. A passed rule writes the season it names and leaves
 *   earlier ones alone, so a keeper cost computed in 2024 stays where it was. `configFor(season)`
 *   derives the config rather than reading a mutated constant — the same principle the rest of
 *   this app already holds to, applied to the rules themselves.
 *
 *   SOME RULES CANNOT BE APPLIED BY CHANGING A NUMBER, and pretending otherwise is the dangerous
 *   part. "Eliminate defenses as a position" moves two parameters and then needs somebody to
 *   remove DEF from scoring and from the lineup. So a `build` effect records the rule, applies
 *   what it can, and carries a checklist of what is still outstanding — visibly, loudly, because
 *   a rule the league voted for and the app does not implement is the failure nobody notices
 *   until a draft is already running.
 *
 * FOUR KINDS, and the fourth is most of the rulebook: `records` — team-name obligations, trade
 * etiquette, how the meeting is run. It joins the rulebook, becomes citable, and changes no
 * value. That is the honest answer for the majority of the league's 141 rules, and saying so
 * plainly stops anybody hunting for a setting that was never going to exist.
 */

import { LEAGUE_2026, type LeagueConfig } from './config.js';

export type EffectKind = 'parameter' | 'table' | 'build' | 'records';

export const KIND_LABEL: Record<EffectKind, string> = {
  parameter: 'parameter',
  table: 'table',
  build: 'needs a build',
  records: 'records only',
};

export const KIND_BLURB: Record<EffectKind, string> = {
  parameter: 'A value that already exists — caps, fines, rounds, deadlines, the FAAB base. '
    + 'Passing it writes the value for the named season.',
  table: 'A shape, not a number — the Grid, the tax curve, lineup slots. The proposal carries a '
    + 'saved diff from that table’s own editor.',
  build: 'No parameter exists yet. The farm system in 2019, the franchise tag in 2025, removing a '
    + 'position. Also repeals that revert to a superseded rule, and interpretations that change '
    + 'how existing logic reads.',
  records: 'Nothing for the app to do. Team-name obligations, trade etiquette, how the meeting is '
    + 'run — anything the league enforces socially. It joins the rulebook, becomes citable, and '
    + 'changes no value.',
};

/** The settings a `parameter` effect may write. Deliberately a closed list. */
export type ParamKey = Extract<keyof LeagueConfig,
  'draftRounds' | 'rosterSize' | 'irSlots' | 'freeKeepers' | 'maxKeepers' | 'faabBase'
  | 'faabPurchaseRate' | 'postDraftDropFine' | 'ownYourSoulFine' | 'maxFarmPlayers'
  | 'regularSeasonWeeks' | 'sleeperMinRound'>;

/**
 * Farm picks per year is not on `LeagueConfig` — it lives in the farm rules — but it is the
 * drawing's own first example and a real docket item this year, so the language has to hold it.
 */
export type ExtraKey = 'farmPicksPerYear' | 'farmRounds';

export type SettingKey = ParamKey | ExtraKey;

export const SETTING_LABEL: Record<SettingKey, string> = {
  draftRounds: 'Draft rounds',
  rosterSize: 'Roster size',
  irSlots: 'IR slots',
  freeKeepers: 'Free keepers',
  maxKeepers: 'Maximum keepers',
  faabBase: 'FAAB base',
  faabPurchaseRate: 'FAAB purchase rate',
  postDraftDropFine: 'Post-draft drop fine',
  ownYourSoulFine: 'Own Your Soul fine',
  maxFarmPlayers: 'Farm slots per GM',
  regularSeasonWeeks: 'Regular season weeks',
  sleeperMinRound: 'Sleeper minimum round',
  farmPicksPerYear: 'Farm picks per year',
  farmRounds: 'Farm rounds',
};

export interface SettingChange {
  key: SettingKey;
  /** What it becomes. The `from` is read off the config for the season, never stored. */
  to: number;
}

export interface BuildTask {
  id: string;
  label: string;
  done?: boolean;
}

export interface ProposalEffect {
  kind: EffectKind;
  /** The season the change first applies to. Constrained — see `seasonOptions`. */
  season: number;
  /** `parameter`: one or more settings written for that season. */
  sets?: SettingChange[];
  /** `table`: which table, and what the diff does, in a sentence. */
  table?: { name: 'grid' | 'luxuryTax' | 'lineup'; note: string };
  /** `build`: what it changes, in the commissioner's own words. */
  changes?: string;
  /** `build`: what has to exist before the rule is real. */
  tasks?: BuildTask[];
}

/* -- which seasons a change may name ---------------------------------------------------------- */

/**
 * Facts about where the offseason has got to, which is what decides whether this season can still
 * take a change.
 *
 * The overlap is the whole reason this control exists: the committee meets on 26 August, the farm
 * auction has not run yet and keeper declarations have. So "farm picks per year" can take 2026
 * and "free keepers" cannot — re-pricing a slate a GM has already submitted is not a rule change,
 * it is moving the goalposts after the whistle.
 */
export interface SeasonFacts {
  /** Keeper declarations are open or already submitted by somebody. */
  declarationsStarted: boolean;
  /** The main draft has been run. */
  draftRun: boolean;
  /** The farm auction has been run. */
  farmRun: boolean;
  /** The regular season has begun — scoring and lineups are frozen for the year. */
  seasonStarted: boolean;
}

export interface SeasonOption {
  season: number;
  ok: boolean;
  /** Why this season is not offered. Empty when it is. */
  why: string;
}

/**
 * Which seasons this change may name, and why one is refused.
 *
 * The refusal reason is the useful half. "2027 season" in a select tells a commissioner nothing;
 * "2026 is not offered — declarations are open and a GM has submitted" tells him what would have
 * to be true, and stops him assuming the app is being cautious for no reason.
 */
export function seasonOptions(
  effect: Pick<ProposalEffect, 'kind' | 'sets' | 'table'>,
  thisSeason: number,
  facts: SeasonFacts,
): SeasonOption[] {
  const blockers: string[] = [];
  const keys = new Set((effect.sets ?? []).map((s) => s.key));

  // Scoring and lineups cannot move once the season is being played.
  if (facts.seasonStarted && (effect.kind === 'build' || effect.table?.name === 'lineup')) {
    blockers.push('scoring cannot change mid-season');
  }
  // Anything that reprices a keeper is out once a slate has been submitted against the old price.
  const repricing = ['freeKeepers', 'maxKeepers', 'sleeperMinRound'] as const;
  if (facts.declarationsStarted
    && (repricing.some((k) => keys.has(k)) || effect.table?.name === 'grid'
      || effect.table?.name === 'luxuryTax')) {
    blockers.push('declarations are open and a GM has submitted');
  }
  if (facts.draftRun && (keys.has('draftRounds') || keys.has('rosterSize'))) {
    blockers.push('the draft has already been run');
  }
  if (facts.farmRun && (keys.has('farmPicksPerYear') || keys.has('farmRounds')
    || keys.has('maxFarmPlayers'))) {
    blockers.push('the farm auction has already run');
  }

  return [
    {
      season: thisSeason,
      ok: blockers.length === 0,
      why: blockers.length === 0
        ? whyThisSeasonWorks(effect, facts)
        : `${thisSeason} is not offered — ${blockers.join('; ')}.`,
    },
    { season: thisSeason + 1, ok: true, why: '' },
  ];
}

function whyThisSeasonWorks(
  effect: Pick<ProposalEffect, 'kind' | 'sets'>, facts: SeasonFacts,
): string {
  const keys = new Set((effect.sets ?? []).map((s) => s.key));
  if (!facts.farmRun && (keys.has('farmPicksPerYear') || keys.has('farmRounds'))) {
    return 'The farm auction has not run yet, so this season can take it.';
  }
  if (!facts.draftRun && (keys.has('draftRounds') || keys.has('rosterSize'))) {
    return 'The draft has not run yet, so this season can take it.';
  }
  return 'Nothing this touches has happened yet, so this season can take it.';
}

/* -- knock-ons ------------------------------------------------------------------------------ */

/**
 * What ELSE moves when this one does.
 *
 * The luxury tax is a curve indexed by keeper count, and the free-keeper number decides where the
 * curve starts. Raise free keepers from four to five and the taxed slots become 6–8: the 5th-slot
 * field is DROPPED, not zeroed, which is the distinction a commissioner will otherwise discover
 * by finding a `₣0` row that looks like a mistake.
 *
 * Derived, never declared. A knock-on somebody had to remember to type is a knock-on that gets
 * forgotten in the one year it matters.
 */
export function knockOn(effect: Pick<ProposalEffect, 'sets'>, base: LeagueConfig): string[] {
  const out: string[] = [];
  for (const s of effect.sets ?? []) {
    if (s.key === 'freeKeepers' && s.to !== base.freeKeepers) {
      const from = base.freeKeepers;
      out.push(
        `Moves the taxed slots to ${s.to + 1}–${base.maxKeepers}. `
        + `The ${from + 1}${ordinal(from + 1)}-slot field is dropped, not zeroed.`,
      );
    }
    if (s.key === 'maxKeepers' && s.to !== base.maxKeepers) {
      out.push(`The tax curve gains or loses rows at the top — it runs to ${s.to} now.`);
    }
    if (s.key === 'draftRounds' && s.to !== base.draftRounds) {
      out.push(
        `The Grid's last row moves to ${s.to}, and a free agent comes in at Round ${s.to + 1}.`,
      );
    }
    if (s.key === 'rosterSize' && s.to !== base.rosterSize) {
      out.push('Roster needs and the bench count on every draft screen follow this.');
    }
  }
  return out;
}

const ordinal = (n: number) => (n % 10 === 1 && n !== 11 ? 'st'
  : n % 10 === 2 && n !== 12 ? 'nd' : n % 10 === 3 && n !== 13 ? 'rd' : 'th');

/* -- the config, by season ------------------------------------------------------------------- */

/** A proposal far enough along to have changed something. */
export interface PassedRule {
  id: string;
  title: string;
  /** `passed` or `provisional`. A provisional pass applies and is queued for re-vote. */
  status: 'passed' | 'provisional';
  effect?: ProposalEffect;
  /** For the "Set by" column — `2026 docket 3a`, and the count that carried it. */
  docketRef?: string;
  tally?: { yes: number; no: number };
}

/**
 * The config as it stands for a given season.
 *
 * DERIVED, NOT STORED, which is the point. `LEAGUE_2026` stays the base — the state of the rules
 * as encoded by hand up to now — and every passed rule that names a season at or before this one
 * is applied on top, in docket order. Ask for 2024 and you get the base; ask for 2027 and you get
 * the base plus everything the committee has voted for it. History cannot move under you.
 */
export function configFor(
  season: number,
  rules: readonly PassedRule[],
  base: LeagueConfig = LEAGUE_2026,
): LeagueConfig {
  let out: LeagueConfig = { ...base };
  for (const r of rules) {
    const e = r.effect;
    if (!e || e.kind === 'records' || e.season > season) continue;
    for (const s of e.sets ?? []) {
      if (s.key in out) out = { ...out, [s.key]: s.to } as LeagueConfig;
    }
    if (e.kind === 'parameter' && (e.sets ?? []).some((s) => s.key === 'freeKeepers')) {
      out = { ...out, luxuryTaxByKeeperCount: retaxed(out) };
    }
  }
  return out;
}

/**
 * The tax curve after the free-keeper line moves.
 *
 * Regenerated rather than edited, because the curve is not a list of numbers somebody chose — it
 * is a step of ₣22 per slot above the free ones (2023 §2), and the numbers in `config.ts` are
 * that rule already applied. Editing them by hand is how the two drift apart.
 */
function retaxed(c: LeagueConfig): Record<number, number> {
  const step = 22;
  const out: Record<number, number> = {};
  for (let n = 0; n <= c.maxKeepers; n += 1) {
    out[n] = n <= c.freeKeepers ? 0 : (n - c.freeKeepers) * step;
  }
  return out;
}

/* -- the 6b table ---------------------------------------------------------------------------- */

export type RowState = 'applied' | 'queued' | 'partly-built' | 'unchanged';

export interface ConfigRow {
  key: SettingKey;
  label: string;
  now: number;
  next: number;
  /** Up, down or level between the two seasons. */
  direction: 'up' | 'down' | 'same';
  /** "Two-round farm draft, 7–2" — the vote that set it. */
  setBy?: string;
  docketRef?: string;
  state: RowState;
  /** For `partly-built`: how many of the rule's tasks are done. */
  built?: { done: number; of: number };
}

const SHOWN: SettingKey[] = [
  'freeKeepers', 'maxKeepers', 'draftRounds', 'rosterSize', 'maxFarmPlayers', 'farmPicksPerYear',
  /* Two for 2026 — the 2025 experiment — and conditional on the 26 August re-vote. */
  'farmRounds',
];

/**
 * One row per setting, two seasons, one state — the table `6b` draws.
 *
 * The state is the column that matters and `partly-built` is the reason the table exists: a rule
 * the league voted for, whose parameters landed, and whose real change is still waiting on
 * somebody to write it. Applied and queued are bookkeeping; that third one is a warning.
 */
export function configRows(
  season: number,
  rules: readonly PassedRule[],
  base: LeagueConfig = LEAGUE_2026,
  /*
     `farmRounds: 2` is the standing value, not the historical one: the 2025 experiment ran two
     and it carries into 2026 unless the committee lets it lapse. Defaulting to 1 here made the
     table state the opposite of what the league is actually running.
  */
  extras: Readonly<Partial<Record<ExtraKey, number>>> = { farmPicksPerYear: 1, farmRounds: 2 },
): ConfigRow[] {
  const now = configFor(season, rules, base);
  const next = configFor(season + 1, rules, base);
  const read = (c: LeagueConfig, k: SettingKey, at: number): number =>
    (k in c ? (c as unknown as Record<string, number>)[k]!
      : lastExtra(k as ExtraKey, at, rules, extras));

  return SHOWN.map((key) => {
    const a = read(now, key, season);
    const b = read(next, key, season + 1);
    const rule = ruleThatSet(key, rules);
    const tasks = rule?.effect?.tasks ?? [];
    const done = tasks.filter((t) => t.done).length;

    const state: RowState = !rule ? 'unchanged'
      : tasks.length > 0 && done < tasks.length ? 'partly-built'
        : (rule.effect?.season ?? season) <= season ? 'applied' : 'queued';

    return {
      key,
      label: SETTING_LABEL[key],
      now: a,
      next: b,
      direction: b > a ? 'up' : b < a ? 'down' : 'same',
      setBy: rule ? summaryOf(rule) : undefined,
      docketRef: rule?.docketRef,
      state,
      ...(state === 'partly-built' ? { built: { done, of: tasks.length } } : {}),
    };
  });
}

function lastExtra(
  key: ExtraKey, season: number, rules: readonly PassedRule[],
  extras: Readonly<Partial<Record<ExtraKey, number>>>,
): number {
  let v = extras[key] ?? 0;
  for (const r of rules) {
    if (!r.effect || r.effect.season > season) continue;
    for (const s of r.effect.sets ?? []) if (s.key === key) v = s.to;
  }
  return v;
}

function ruleThatSet(key: SettingKey, rules: readonly PassedRule[]): PassedRule | undefined {
  let hit: PassedRule | undefined;
  for (const r of rules) {
    if ((r.effect?.sets ?? []).some((s) => s.key === key)) hit = r;
  }
  return hit;
}

const summaryOf = (r: PassedRule) =>
  `${r.title}${r.tally ? `, ${r.tally.yes}–${r.tally.no}` : ''}`;

/**
 * Rules that passed and are not yet real.
 *
 * The loudest card on the screen, deliberately. A rule the league voted for and the app does not
 * implement is the failure nobody notices until a draft is underway — and the app disagreeing
 * with the rulebook is worse than either being wrong on its own, because both look right.
 */
export interface Stuck {
  rule: PassedRule;
  done: BuildTask[];
  todo: BuildTask[];
  /** The season it is needed by. */
  by: number;
}

export function notYetActive(rules: readonly PassedRule[]): Stuck[] {
  return rules
    .filter((r) => (r.effect?.tasks ?? []).some((t) => !t.done))
    .map((r) => ({
      rule: r,
      done: (r.effect!.tasks ?? []).filter((t) => t.done),
      todo: (r.effect!.tasks ?? []).filter((t) => !t.done),
      by: r.effect!.season,
    }));
}
